<script setup lang="ts">
import { dashboardApiClient } from '@api-client/dashboard';
import type { ResType } from '@api-client/typeUtils';
import { currentWebsiteId } from '@partner-spa-vue/stores/MyWebsitesStore';
import { onMounted, ref } from 'vue';
import { toast } from '@/ui-plus/sonner';
import type { ServiceFee } from '@lib/zod/serviceFee';
import ServiceFeeDialog from '../ServiceFeeDialog.vue';
// import PlatformServiceFeeOverrideDialog from './PlatformServiceFeeOverrideDialog.vue';
import { Badge } from '@/components/ui/badge';
import {
    zmgAdminOnlyCacheUpdateClient,
    zmgPublicCacheClient
} from '@api-client/zmg_cache';
// import { authState } from '@/ui-plus/useAuth';

type StoresResponse = ResType<typeof dashboardApiClient.website.supported_stores.list[':websiteId']['$get']>["data"];

const props = defineProps<{
    store: StoresResponse['stores'][number];
}>();

const moreStoreDetails = ref<{
    overridePartnerFee?: ServiceFee;
}>({
    overridePartnerFee: undefined,
});

// const isPlatformFeeAdmin = authState.value.accessTokenPayload?.email === 'kerem-dev2@faircarts.com' || // DEV
//     authState.value.accessTokenPayload?.email === 'onlinebite45@gmail.com'; // PROD

onMounted(async () => {
    const { data, error } = await (await zmgPublicCacheClient.website.fee_override_as_store_support_team[':id'][':cId']['data.json'].$get({
        param: { id: currentWebsiteId.value ?? '', cId: props.store.id },
    })).json();
    if (error) {
        console.error(error);
        toast.error('Failed to load store allowlist');
    }
    if (data) {
        console.log('data', data);
        moreStoreDetails.value.overridePartnerFee = data.overridePartnerFee ?? undefined
    }
});

const upsertStore = async (storeId: string, overridePartnerFee?: ServiceFee) => {
    const { error } = await (await zmgAdminOnlyCacheUpdateClient.website.fee_override_as_store_support_team[':id'][':cId'].$patch({
        param: { id: currentWebsiteId.value ?? '', cId: storeId },
        json: { overridePartnerFee: overridePartnerFee ?? null }
    })).json();
    if (error) {
        console.error(error);
        toast.error('Failed to save store');
    } else {
        toast.success('saved');
        moreStoreDetails.value.overridePartnerFee = overridePartnerFee ?? undefined;
    }
};

</script>

<template>
    <div class="flex flex-col">
        <span class="font-medium">{{ store.name }}</span>
        <span v-if="store.address1"
              class="text-sm text-muted-foreground">
            {{ store.address1 }}
        </span>
        <span v-if="store.city"
              class="text-sm text-muted-foreground">
            {{ store.city }}
        </span>
        <span v-if="store.country"
              class="text-sm text-muted-foreground">
            {{ store.country }}
        </span>
        <span class="text-sm text-muted-foreground">
            ({{ store.isActive === true ? 'Active' : 'Inactive' }})
        </span>
        <span v-if="store.isSuspended === true"
              class="text-xs text-destructive">
            <OctagonX class="h-4 w-4 mr-1" />
            Suspended
        </span>
        <span class="text-xs text-muted-foreground">{{ store.id }}</span>
    </div>
    <div class="flex items-center gap-2">
        <div v-if="moreStoreDetails.overridePartnerFee">
            <Badge variant="outline">
                {{ moreStoreDetails.overridePartnerFee?.ratePerOrder }}%
            </Badge>
            <Badge variant="outline">
                +{{ moreStoreDetails.overridePartnerFee?.feePerOrder }}
            </Badge>
        </div>
        <ServiceFeeDialog :id="store.id"
                          :override-fee="moreStoreDetails.overridePartnerFee ?? {}"
                          @upsert="(id, overridePartnerFee) => {
                            console.log('overridePartnerFee', overridePartnerFee);
                            moreStoreDetails.overridePartnerFee = overridePartnerFee ?? undefined;
                            upsertStore(id, overridePartnerFee ?? undefined);
                        }" />
        <!-- <PlatformServiceFeeOverrideDialog v-if="isPlatformFeeAdmin"
                                          seller-type="store"
                                          :seller-id="store.id" /> -->
    </div>
</template>
